import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { Popover, Button, Subheading, Paragraph, IconButton } from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import { MagicWandIcon } from '@treams/treams-icons';
import useLanguage from 'packages/kakapo-ui-application/hooks/useLanguage';
import { MagicChatModal } from './MagicChatModal';

const GoalSuggestionContainer = styled(Box)`
	max-width: 80%;
	background: ${(props) => props.theme.colors.white};
	border-radius: 8px;
	padding: 15px 18px;
`;

const SubgoalLabel = styled(Paragraph)`
	display: flex;
	flex-direction: row;
	margin-bottom: 6px;

	&::before {
		--size: 18px;
		content: '';
		display: block;
		width: var(--size);
		height: var(--size);
		border-radius: 4px;
		border: 2px solid ${(props) => props.theme.colors.lightBlue};
		background: ${(props) => props.theme.colors.whiteBlue};
		box-sizing: border-box;
		margin-right: 8px;
		flex-shrink: 0;
		margin-top: calc(12px - var(--size) / 2);
	}
`;

function GoalSuggestion(props: {
	data: graphql.GoalSuggestion;
	onTake: (suggestion: graphql.GoalSuggestion) => void;
}) {
	const { t } = useTranslation();

	return (
		<>
			<GoalSuggestionContainer mb={2}>
				<Subheading mb={1}>{props.data.title}</Subheading>
				<Paragraph mb={3}>{props.data.description}</Paragraph>
				{props.data.subgoals.map((subgoal, idx) => (
					<SubgoalLabel key={idx}>{subgoal}</SubgoalLabel>
				))}
			</GoalSuggestionContainer>
			<Flex>
				<Button
					onClick={async () => await props.onTake(props.data)}
					title={t('goals.magic.take')}
				/>
			</Flex>
		</>
	);
}

export default function GoalMagic(props: {
	onTakeSuggestion: (goal: graphql.GoalSuggestion) => void;
	initialInput?: string;
}) {
	const language = useLanguage();
	const [visible, setVisible] = useState(props.initialInput !== undefined);
	const [start, { loading: starting }] = graphql.useStartGoalSuggestionsMutation();
	const [advance, { loading: advancing }] = graphql.useAdvanceGoalSuggestionsMutation();

	const onTakeSuggestion = useCallback(
		async (suggestion) => {
			await props.onTakeSuggestion(suggestion);
			setVisible(false);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[setVisible],
	);

	return (
		<Popover.Target
			contents={
				<MagicChatModal<graphql.GoalSuggestion>
					translationKey="goals"
					initialInput={props.initialInput}
					onDismiss={() => setVisible(false)}
					startChat={async (reset) => {
						const response = await start({
							variables: {
								input: {
									locale: language.id,
									reset,
								},
							},
						});

						return { chat: response.data!.startGoalSuggestions.chat };
					}}
					starting={starting}
					advanceChat={async (chatID, userResponse) => {
						const response = await advance({
							variables: {
								input: {
									locale: language.id,
									chatID,
									userResponse,
								},
							},
						});

						return {
							chat: response.data!.advanceGoalSuggestions.chat,
							suggestion: response.data!.advanceGoalSuggestions.suggestion ?? null,
						};
					}}
					advancing={advancing}
					renderSuggestion={(suggestion) => (
						<GoalSuggestion data={suggestion} onTake={onTakeSuggestion} />
					)}
				/>
			}
			visible={visible}
			center
		>
			<IconButton icon={<MagicWandIcon />} type="primary" onClick={() => setVisible(true)} />
		</Popover.Target>
	);
}
